/**
 * Who sees which tools — the ONE place the sidebar, the page locks and the login
 * landing ask.
 *
 *   isSuperAdmin     the backend answered "super_admin" — every surface, always.
 *   isPracticeAdmin  superuser, or the backend lists at least one office the
 *                    user administers (/clinics/administered-offices/).
 *   isFTCPersonnel   the user holds an FTC-Personnel role. NB the sidebar also
 *                    raises this flag for super admins so they can reach the FTC
 *                    Panel, which is why isSuperAdmin is asked FIRST here — a
 *                    super admin is never "an FTC" for this purpose.
 *   features         the office's per-feature modes from /clinics/office-features/
 *                    ({payments_ar: "default"|"on"|"off", ...}); missing = default.
 *
 * User management and billing are the practice's own and are never shown to FTC
 * personnel — the backend refuses them too (PracticeAdminAccess,
 * usage._user_owns_office) — while the insurance-verification queue is work an
 * FTC does for the office and stays.
 *
 * The four OFFICE_FEATURES are switched per office by a super admin (CRM office
 * drawer → Features). "Default" keeps today's behaviour: Payments & AR and
 * Treatment Planning for everyone, Targets & Rewards and the Automation Engine for
 * super admins only. Super admins always see every feature. Enforcement is this
 * menu plus a page lock (FeatureGate); the backend APIs are unchanged by design.
 */
export const OFFICE_FEATURES = [
  { key: "payments_ar", access: "paymentsAr", label: "Payments & AR", byDefault: "everyone" },
  { key: "treatment_planning", access: "treatmentPlanning", label: "Treatment Planning", byDefault: "everyone" },
  { key: "targets_rewards", access: "targetsRewards", label: "Targets and Rewards", byDefault: "super_admin" },
  { key: "automation_engine", access: "automationEngine", label: "Automation Engine", byDefault: "super_admin" },
];

const FEATURE_BY_KEY = OFFICE_FEATURES.reduce((acc, f) => ({ ...acc, [f.key]: f }), {});

/** Whether ``feature`` is on for this viewer in this office. */
export function featureOn(featureKey, { isSuperAdmin, features } = {}) {
  const feature = FEATURE_BY_KEY[featureKey];
  if (!feature) return false;
  if (isSuperAdmin) return true;
  const mode = features && features[featureKey];
  if (mode === "on") return true;
  if (mode === "off") return false;
  return feature.byDefault === "everyone";
}

export function sidebarAccess({ isPracticeAdmin, isFTCPersonnel, isSuperAdmin, features } = {}) {
  const featureFlags = OFFICE_FEATURES.reduce(
    (acc, f) => ({ ...acc, [f.access]: featureOn(f.key, { isSuperAdmin, features }) }),
    {},
  );
  if (isSuperAdmin) {
    return { insuranceVerification: true, userManagement: true, billing: true, ...featureFlags };
  }
  const admin = !!isPracticeAdmin;
  const ftc = !!isFTCPersonnel;
  return {
    insuranceVerification: admin || ftc,
    userManagement: admin && !ftc,
    billing: admin && !ftc,
    ...featureFlags,
  };
}

/**
 * What a gated page should render.
 *   "loading"     nothing known yet for this office (first visit, no cache)
 *   "allow"       show the page
 *   "disabled"    the feature is off for this office
 *   "no_banking"  Payments & AR, but the office's bank account isn't configured
 *   "plan_required"  the office has no plan and no trial left (usage.services.access);
 *                 the page shows a Choose-a-plan stage instead
 *
 * A super admin is always allowed. An unknown banking state (null — the check
 * failed or the backend is older) allows: the Payments APIs still refuse to move
 * money without banking, so failing open costs nothing.
 */
export function pageAccess(featureKey, { ready, failed, isSuperAdmin, features, bankingConfigured, plan } = {}) {
  if (isSuperAdmin) return "allow";
  // Couldn't learn the office's switches (network, older backend): open the page
  // exactly as today rather than lock anyone out of their own tools.
  if (failed) return "allow";
  if (!ready) return "loading";
  if (!featureOn(featureKey, { isSuperAdmin, features })) return "disabled";
  // `gated` is already THIS viewer's answer (never true for super admins or FTC).
  if (plan && plan.gated) return "plan_required";
  if (featureKey === "payments_ar" && bankingConfigured === false) return "no_banking";
  return "allow";
}

/** Is this viewer sent to the plans for this office? Menu clicks and the
 * Insurance Verification actions ask this before doing anything. */
export function planGated(status) {
  return !!(status && !status.isSuperAdmin && status.plan && status.plan.gated);
}

/** Where to send someone whose usual page is switched off for their office. */
export function landingRoute(access = {}) {
  if (access.treatmentPlanning) return "/treatment-plans";
  if (access.insuranceVerification) return "/insurance-requests";
  if (access.paymentsAr) return "/ar";
  return "/office/profile";
}
